import { forwardRef, useEffect, useImperativeHandle, useState, useRef } from 'react';
import { Modal, Button, Form, Input, Checkbox, Spin, Alert, message, Tabs } from 'antd';
import { BASE_URL, LOGION_URL, OTP_URL } from '../../constants/Url'
import PhoneInput from '../InputField/PhoneInput';
import { useTranslation } from 'react-i18next';
import RegisterModal from './RegisterModal';
import { loginSuccessAction } from '../../modules/auth/actions';
import { storageKeys } from '../../constants/storage';
import { handleLogout } from '../../utils/storage';
import { useDispatch, useSelector } from 'react-redux';
import httpClient from '../../utils/HttpClient';
import { useNavigate } from 'react-router';

const AuthModal = forwardRef(({ onAuthSuccess, onClose }, ref) => {
    const user = useSelector((state) => state.authReducer);
    let token = user && user?.token ? user?.token : null
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [open, setOpen] = useState(false);
    const [countryCode, setCountryCode] = useState('86');
    const [phone, setPhone] = useState('');
    const [smsCode, setSmsCode] = useState('')
    const [email, setEmail] = useState('');
    const [emailCode, setEmailCode] = useState('');
    const [isAgree, setIsAgree] = useState(false);
    const [activeTab, setActiveTab] = useState('phone');
    const [loading, setLoading] = useState(false);
    const [loadingNew, setLoadingNew] = useState(false);
    const [error, setError] = useState(null);
    const [view, setView] = useState('MAIN');
    const [countdown, setCountdown] = useState(0);
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const getPhoneLength = () => {
        switch (countryCode) {
            case '86':
                return 11; // China
            case '852':
                return 8; // Hong Kong
            default:
                return 11;
        }
    };

    const isValidPhone = () => {
        return phone?.length === getPhoneLength();
    };

    useEffect(() => {
        let timer;
        if (countdown > 0) {
            timer = setInterval(() => {
                setCountdown(prev => prev - 1);
            }, 1000);
        }
        return () => clearInterval(timer);
    }, [countdown]);

    const prevTokenRef = useRef(token);
    // Clear all inputs when user logs out
    useEffect(() => {
        if (prevTokenRef.current && !token) {
            setOpen(false);
            setPhone('');
            setSmsCode('');
            setEmail('');
            setEmailCode('');
            setIsAgree(false);
            setCountryCode('86');
            setCountdown(0);
            setError(null);
            form.resetFields();
        }
        prevTokenRef.current = token;
    }, [token, form]);

    const handleClose = (isSuccess) => {
        setActiveTab('phone');
        if (loading) return;
        // If closing the registration modal without completing registration, log out so user cannot use the system without registering
        if (!isSuccess && token && !user?.user?.completedTS && view === 'REGISTER') {
            handleLogout();
        }
        const wasRegisterView = view === 'REGISTER';
        setOpen(false);
        setError(null);
        setLoading(false);
        setCountdown(0);
        if (isSuccess && wasRegisterView) {
            onAuthSuccess?.();
        } else if (!isSuccess) {
            onClose?.();
        }
    };

    const handleLoginSuccess = () => {
        handleClose(true);
        onAuthSuccess?.();
    };

    const handleShow = (type) => {
        setOpen(true);
        setView(type ? type : 'MAIN');
        if (user?.user?.email && !user?.user?.phone?.number) {
            setActiveTab('email');
        }
    };

    useImperativeHandle(ref, () => ({
        open: handleShow,
        close: (isSuccess = true) => handleClose(isSuccess)
    }));

    const saveAuth = (token, newUser, refreshToken) => {
        const existingUser = user?.user || {};
        const mergedUser = {
            ...existingUser,
            ...newUser,
            email: newUser?.email || existingUser?.email || '',
            phone: newUser?.phone || existingUser?.phone || null
        };
        dispatch(loginSuccessAction({ token, user: mergedUser, photo: mergedUser?.picUrl }));
        if (token) localStorage.setItem(storageKeys.TOKEN, btoa(token));
        if (refreshToken) localStorage.setItem(storageKeys.TOKEN_RE, btoa(refreshToken));
        localStorage.setItem(storageKeys.USER, JSON.stringify(mergedUser));
    };

    const handleEmailContinue = async () => {
        if (!email || !emailCode || !isAgree) return;
        
        setError(null);
        setLoading(true);
        try {
            // Note: Adjust payload structure based on your actual backend Email Login implementation
            const res = await httpClient.post(LOGION_URL, {
                email: email,
                code: emailCode
            })
                .then(res => res.data)
                .catch(error => ({ status: 500, error }));
                
            if (res?.status === 200 || res?.code === 200) {
                if (!res?.member?.completedTS) {
                    saveAuth(res?.accessToken, res?.member, res?.refreshToken);
                    setView('REGISTER');
                } else {
                    saveAuth(res?.accessToken, res?.member, res?.refreshToken);
                    handleLoginSuccess();
                }
            } else {
                setError(t(res?.error?.message || res?.message || t("Failed to login with email")));
            }
            setLoading(false);
        } catch (error) {
            setLoading(false);
            setError(t("Failed to login with email"));
        }
    };

    const handlePhoneContinue = async () => {
        if (!phone || !smsCode || !isAgree) return;
        
        if (!isValidPhone() || smsCode.length !== 6) {
            message.error(t("Please enter a valid phone number and verification code"));
            return false;
        }
        setError(null);
        setLoading(true);
        try {
            const res = await httpClient.post(LOGION_URL, {
                "phone": {
                    "countryCode": countryCode,
                    "number": phone
                },
                "code": `${smsCode}`
            })
                .then(res => res.data)
                .catch(error => ({ status: 500, error }));
            if (res?.status === 200) {
                if (!res?.member?.completedTS) {
                    saveAuth(res?.accessToken, res?.member, res?.refreshToken);
                    setView('REGISTER');
                } else {
                    // login success
                    saveAuth(res?.accessToken, res?.member, res?.refreshToken);
                    handleLoginSuccess();
                }

            } else {
                setError(t(res?.error?.message || res?.message || t("Failed to login")));
                // message.error(t(res?.error?.message || res?.message || "Failed to login"));
            }
            setLoading(false);
        } catch (error) {
            setLoading(false);
            setError(t("Failed to login"));
            // message.error(t("Failed to login"));
        }
    };

    const handleSendCode = async () => {
        if (!phone) {
            message.warning(t("text_enter_phone"));
            return false;
        }
        if (!isValidPhone()) {
            message.warning(t("Please enter a valid phone number and verification code"));
            return false;
        }
        if (countdown > 0 || loadingNew) {
            return false;
        }
        try {
            setError(null);
            setLoadingNew(true);
            const res = await httpClient.post(OTP_URL, {
                "phone": {
                    "countryCode": countryCode,
                    "number": phone
                }
            })
                .then(res => res.data)
                .catch(error => ({ status: 500, error }));
            if (res?.status === 200) {
                message.success(t(res?.message || "Verification code sent successfully"));
                setCountdown(60);
                setLoadingNew(false);
            } else {
                message.error(t(res?.error?.message || res?.message || "Failed to send verification code"));
                setLoadingNew(false);
            }
        } catch (error) {
            message.error(t("Failed to send verification code"));
            setLoadingNew(false)
        }
    }

    const handleSendEmailCode = async () => {
        if (!email) {
            message.warning(t("text_enter_email"));
            return false;
        }
        if (countdown > 0 || loadingNew) {
            return false;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            message.error(t("text_enter_valid_email"));
            return false;
        }
        try {
            setError(null);
            setLoadingNew(true);
            const res = await httpClient.post('/auth/request-otp-mail', { email })
                .then(res => ({ success: true, data: res.data }))
                .catch(error => ({ success: false, error: error?.response?.data || error }));
                
            if (res.success) {
                message.success(t(res.data?.message || "Verification code sent successfully"));
                setCountdown(60);
                setLoadingNew(false);
            } else {
                message.error(t(res.error?.message || res.error?.error || "Failed to send verification code"));
                setLoadingNew(false);
            }
        } catch (error) {
            message.error(t("Failed to send verification code"));
            setLoadingNew(false);
        }
    }

    const renderView = () => {
        switch (view) {
            case 'REGISTER':
                return <RegisterModal
                    // phone={phone}
                    mode={activeTab}
                    setLoading={setLoading}
                    loading={loading}
                    onAuthSuccess={saveAuth}
                    handleCloses={handleClose}
                    setView={setView}
                // onBack={() => setView('MAIN')}
                />;
            default:
                return (
                    <div style={{ padding: "20px 20px 0px 20px" }}>
                        <div style={{ textAlign: 'center', marginBottom: 30 }}>
                            <div className='flex items-center mb-3' style={{ display: 'flex', justifyContent: 'center' }}>
                                <img alt='ESI-Logo' role='button' src={BASE_URL + "/logo.svg"} className='esi-logo' />
                            </div>
                            <p style={{ color: '#888' }}>
                                {t("text_leading_online_hair_magazine")}
                            </p>
                        </div>

                        {error && <Alert type="error" message={error} showIcon style={{ marginBottom: 16 }} />}

                        <div style={{ display: 'flex', alignItems: 'center', marginTop: '10px' }}>
                            <p className='labelLogin'>{t("text_login_reg_details")}</p>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: "25px" }}>
                            <p style={{ color: '#888' }}>
                                {activeTab === 'phone' 
                                    ? t("text_login_reg_details2") 
                                    : t("text_enter_email_password")}
                            </p>
                        </div>

                        <Form form={form} layout="vertical">
                            {/* Inline Text Tabs directly above input */}
                            <div className="flex items-center gap-2 mb-2 text-[14px]">
                                <span 
                                    className={`cursor-pointer ${activeTab === 'phone' ? 'font-bold text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
                                    onClick={() => { setActiveTab('phone'); setError(null); }}
                                >
                                    {t('text_phone_login')}
                                </span>
                                <span className="text-gray-300">|</span>
                                <span 
                                    className={`cursor-pointer ${activeTab === 'email' ? 'font-bold text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
                                    onClick={() => { setActiveTab('email'); setError(null); }}
                                >
                                    {t('text_email_login')}
                                </span>
                            </div>

                            {activeTab === 'phone' ? (
                                <>
                                    <Form.Item name="phone">
                                        <PhoneInput
                                            phone={phone}
                                            setPhone={(val) => { setError(null); setPhone(val); }}
                                            t={t}
                                            disabled={countdown > 0}
                                            countryCode={countryCode}
                                            setCountryCode={(val) => { setError(null); setCountryCode(val); setPhone(''); }}
                                        />
                                    </Form.Item>
                                    <Form.Item name="smsCode">
                                        <Input
                                            size="large"
                                            placeholder={t("text_enter_confirm_code")}
                                            maxLength={6}
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                            suffix={
                                                <span
                                                    role="button"
                                                    tabIndex={0}
                                                    onMouseDown={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                    }}
                                                    onTouchEnd={(e) => {
                                                        e.stopPropagation();
                                                    }}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        if (loadingNew || countdown > 0) return;
                                                        if (!phone) {
                                                            message.warning(t("text_enter_phone"));
                                                            return;
                                                        }
                                                        if (!isValidPhone()) {
                                                            message.warning(t("Please enter a valid phone number and verification code"));
                                                            return;
                                                        }
                                                        handleSendCode();
                                                    }}
                                                    style={{
                                                        color: isValidPhone() && countdown === 0 && !loadingNew ? '#4196ff' : '#999',
                                                        cursor: countdown > 0 || loadingNew ? 'not-allowed' : 'pointer',
                                                        userSelect: 'none',
                                                        padding: '2px 4px',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                    }}
                                                >
                                                    {loadingNew ? <Spin size="small" /> : countdown > 0 ? `${countdown}s` : t("text_get_code")}
                                                </span>
                                            }
                                            value={smsCode}
                                            onChange={(e) => {
                                                setError(null);
                                                const value = e.target.value.replace(/\D/g, '');
                                                setSmsCode(value);
                                                form.setFieldsValue({ smsCode: value });
                                            }}
                                        />
                                    </Form.Item>
                                </>
                            ) : (
                                <>
                                    <Form.Item name="email">
                                        <Input autoComplete="off" size="large" placeholder={t("text_enter_email")} value={email} onChange={(e) => { setEmail(e.target.value); setError(null); }} />
                                    </Form.Item>
                                    <Form.Item name="emailCode">
                                        <Input
                                            size="large"
                                            placeholder={t("text_enter_confirm_code")}
                                            maxLength={6}
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                            suffix={
                                                <span
                                                    role="button"
                                                    tabIndex={0}
                                                    onMouseDown={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                    }}
                                                    onTouchEnd={(e) => {
                                                        e.stopPropagation();
                                                    }}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        if (loadingNew || countdown > 0) return;
                                                        if (!email) {
                                                            message.warning(t("text_enter_email"));
                                                            return;
                                                        }
                                                        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                                                        if (!emailRegex.test(email)) {
                                                            message.warning(t("text_enter_valid_email"));
                                                            return;
                                                        }
                                                        handleSendEmailCode();
                                                    }}
                                                    style={{
                                                        color: email && countdown === 0 && !loadingNew ? '#4196ff' : '#999',
                                                        cursor: countdown > 0 || loadingNew ? 'not-allowed' : 'pointer',
                                                        userSelect: 'none',
                                                        padding: '2px 4px',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                    }}
                                                >
                                                    {loadingNew ? <Spin size="small" /> : countdown > 0 ? `${countdown}s` : t("text_get_code")}
                                                </span>
                                            }
                                            value={emailCode}
                                            onChange={(e) => {
                                                setError(null);
                                                const value = e.target.value.replace(/\D/g, '');
                                                setEmailCode(value);
                                                form.setFieldsValue({ emailCode: value });
                                            }}
                                        />
                                    </Form.Item>
                                </>
                            )}

                            <Form.Item>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <Checkbox checked={isAgree} onChange={(e) => { setError(null); setIsAgree(e.target.checked) }} />
                                    <span style={{ marginLeft: 8, display: 'flex' }}>
                                        {t("text_read_agree")}
                                        <a href='/privacy_statement' target={"_blank"} style={{ color: "#4196ff", marginLeft:"0.2rem" }}>{t("text_privacy_policy")}</a>
                                    </span>
                                </div>
                            </Form.Item>

                            {activeTab === 'phone' ? (
                                <Button type="primary" size='large' block loading={loading} onClick={handlePhoneContinue} disabled={loading || !phone || !isValidPhone() || !smsCode || !isAgree || smsCode?.length !== 6}>
                                    {t('text_login')}
                                </Button>
                            ) : (
                                <Button type="primary" size='large' block loading={loading} onClick={handleEmailContinue} disabled={loading || !email || !emailCode || !isAgree || emailCode?.length !== 6}>
                                    {t('text_login')}
                                </Button>
                            )}
                            {/* {
                                activeTab === 'email' && <div style={{ textAlign: 'center', marginTop: 10, justifyContent: 'center', display: 'flex' }}>
                                    <Form.Item style={{ marginBottom: 0 }}>
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <span style={{ marginLeft: 8, display: 'flex' }}>
                                                {t("text_no_account")}
                                                <a href='#' onClick={(e) => { e.preventDefault(); setView('REGISTER'); }} style={{ color: "#4196ff", marginLeft: 4 }}>{t("text_register")}</a>
                                            </span>
                                        </div>
                                    </Form.Item>
                                </div>
                            } */}
                        </Form>
                    </div>
                );
        }
    };

    return (
        <Modal
            open={open}
            onCancel={() => handleClose(false)}
            closable={true}
            footer={null}
            centered
        // loading={true}
        >
            <Spin spinning={loading}>
                {renderView()}
            </Spin>
        </Modal>
    );
});

export default AuthModal;